import React from 'react';

import {
  Breakpoints,
  CTA,
  TextAppearance,
  TextColor,
  TextCustom,
  TextLineHeight,
  TextSize,
  TextWeight,
  ThemeProvider,
} from '@vw-marketing/us-components';

import { applyTextTransformations } from '../../../../shared/utils/applyTextTransformations';
import {
  StyledConfirmButtonsContainer,
  StyledConfirmInformationContainer,
  StyledDeactivateAccountConfirmationContainer,
} from './deactivate-account-confirmation-styles';
import { UserData } from '../../../../shared/typings/main';

interface DeactivateAccountConfirmationViewProps {
  authService: any;
  translationHandler: (key: string) => string;
  isDesktop: boolean;
  localeCode: string;
  logout: () => void;
  user: UserData | null;
  userRole: string | undefined;
  handleConfirmDeactivation: () => void;
  handleDeactivateAction: () => void;
  setIsDeactivating: (value: boolean) => void;
  onSuccess: () => void;
  onError: () => void;
  onClose: () => void;
}

export const DeactivateAccountConfirmationView = ({
  authService,
  translationHandler,
  isDesktop,
  localeCode,
  logout,
  user,
  userRole,
  handleConfirmDeactivation,
  handleDeactivateAction,
  setIsDeactivating,
  onSuccess,
  onError,
  onClose,
}: DeactivateAccountConfirmationViewProps) => {
  const handleDeactivateAccount = async () => {
    try {
      const userId = user?.id || user?.vwId;

      if (!userId) {
        console.error('Unable to deactivate account: User ID not found');
        return;
      }

      // * For local Only
      // const { removeAllVehiclesFromUser } = await authService.mockRemoveAllVehiclesFromUser(userId);
      // * For Integration and Prod
      const { removeAllVehiclesFromUser } = await authService.removeAllVehiclesFromUserGqlRequest(userId, localeCode);

      if (removeAllVehiclesFromUser?.status === 'Success') {
        onSuccess();

        // Add one second delay before logout to show success message
        // ? Comment on local
        setTimeout(async () => { await logout(); }, 1500);
      } else {
        onDeactivateError();
        console.error('Failed to remove vehicles from user:', userId);
      }
    } catch (error) {
      onDeactivateError();
      console.error('Error deactivating user account:', error);
    }
  };

  const onDeactivateError = () => {
    onClose();
    setIsDeactivating(false);
    onError();
  };

  return (
    <ThemeProvider theme="main">
      <StyledDeactivateAccountConfirmationContainer>
        <StyledConfirmInformationContainer>
          <TextCustom
            appearance={TextAppearance.copy200}
            style={{
              [Breakpoints.default]: {
                fontSize: TextSize.TextSize16,
                fontWeight: TextWeight.regular,
                color: TextColor.primary,
                lineHeight: TextLineHeight.TextLineHeight24,
              },
              [Breakpoints.b1920]: {
                fontSize: TextSize.TextSize20,
                fontWeight: TextWeight.regular,
                color: TextColor.primary,
                lineHeight: TextLineHeight.TextLineHeight32,
              },
            }}
          >
            {applyTextTransformations(
              translationHandler(userRole === 'additional'
                ? 'accountSettings.deactivate.myvwaccount.additional.confirmation.description'
                : 'accountSettings.deactivate.myvwaccount.confirmation.description'
              ),
              ['myVW'],
              ['cannot be undone'],
            )}
          </TextCustom>
        </StyledConfirmInformationContainer>
        <StyledConfirmButtonsContainer>
          <CTA
            tag='button'
            stretchContent={!isDesktop}
            onClick={() => {
              handleConfirmDeactivation();
              handleDeactivateAccount();
              setIsDeactivating(true);
            }}
          >
            {applyTextTransformations(translationHandler('accountSettings.deactivate.myvwaccount.title'), ['myVW'])}
          </CTA>
          <CTA emphasis='secondary' onClick={handleDeactivateAction} stretchContent={!isDesktop} tag='button'>
            {translationHandler('accountSettings.deactivate.myvwaccount.goBack.label')}
          </CTA>
        </StyledConfirmButtonsContainer>
      </StyledDeactivateAccountConfirmationContainer>
    </ThemeProvider>
  );
};
